import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { Eye, BookOpen, Users, Sparkles } from 'lucide-react';

/**
 * Social proof banner — 4 stats animated counters.
 *
 * 2026-05-25 — Audit design (C·01, C·04) :
 *   - Plus utilisé en home (déplacé en footer pour ne pas concurrencer le hero).
 *   - Le tone 'teal' a été renommé 'bordeaux' (la clé teal n'était qu'un
 *     alias historique vers bordeaux dans tailwind.config.js).
 *   - Conservé également : version compact pour usage footer.
 *
 * @param {object} props
 * @param {string} [props.className]
 * @param {boolean} [props.compact]  Plus serré, sans animation d'entrée — pour footer.
 *
 * @example
 *   <SocialProofBanner />
 *   <SocialProofBanner compact />
 */

const STATS = [
  {
    id: 'visitors',
    icon: Eye,
    value: 8000,
    suffix: '+',
    label: { fr: 'visiteurs / mois', en: 'visitors / month', es: 'visitantes / mes' },
    tone: 'bordeaux',
  },
  {
    id: 'articles',
    icon: BookOpen,
    value: 75,
    suffix: '+',
    label: { fr: 'articles SEO publiés', en: 'SEO articles published', es: 'artículos SEO publicados' },
    tone: 'bordeaux',
  },
  {
    id: 'subscribers',
    icon: Users,
    value: 500,
    suffix: '+',
    label: { fr: 'abonnés newsletter', en: 'newsletter subscribers', es: 'suscriptores newsletter' },
    tone: 'gold',
  },
  {
    id: 'guides',
    icon: Sparkles,
    value: 17,
    suffix: '',
    label: { fr: 'mémos PDF disponibles', en: 'PDF guides available', es: 'guías PDF disponibles' },
    tone: 'navy',
  },
];

const TONE_STYLES = {
  bordeaux: 'text-bordeaux-700 dark:text-bordeaux-400',
  gold: 'text-gold-700 dark:text-gold-400',
  navy: 'text-navy-900 dark:text-cream-50',
};

function useAnimatedCount(target, durationMs = 1200, shouldAnimate = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldAnimate) {
      setCount(0);
      return undefined;
    }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / durationMs);
      // easeOutCubic for natural deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, shouldAnimate]);

  return count;
}

function StatTile({ stat, lang, shouldAnimate }) {
  const Icon = stat.icon;
  const count = useAnimatedCount(stat.value, 1500, shouldAnimate);
  const formattedCount = count.toLocaleString('fr-FR');
  return (
    <div className="flex items-center gap-3 rounded-card border border-navy-900/10 bg-cream-50 p-4 dark:border-white/10 dark:bg-navy-900/30">
      <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${TONE_STYLES[stat.tone]}`}>
        <Icon size={18} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className={`font-mono text-xl font-bold tabular-nums leading-none md:text-2xl ${TONE_STYLES[stat.tone]}`}>
          {formattedCount}{stat.suffix}
        </p>
        <p className="mt-1 text-[11px] uppercase tracking-eyebrow text-gray-700 dark:text-white/60">
          {stat.label[lang] || stat.label.fr}
        </p>
      </div>
    </div>
  );
}

export default function SocialProofBanner({ className = '', compact = false }) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const reduce = useReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const node = document.getElementById('social-proof-banner');
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const shouldAnimate = visible && !reduce && !compact;

  if (compact) {
    // Bande discrète pour footer : 4 stats sur 1 ligne, valeurs figées,
    // sans iconône ni anim. Ton uniforme cream-50 (lisible sur navy).
    return (
      <section
        className={`grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-4 ${className}`}
        aria-label={lang === 'en' ? 'Inside Panama in numbers' : lang === 'es' ? 'Inside Panama en cifras' : 'Inside Panama en chiffres'}
      >
        {STATS.map((stat) => (
          <div key={stat.id} className="flex items-baseline gap-3">
            <p className="font-mono text-2xl font-bold tabular-nums leading-none text-cream-50">
              {stat.value.toLocaleString('fr-FR')}{stat.suffix}
            </p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-cream-50/55">
              {stat.label[lang] || stat.label.fr}
            </p>
          </div>
        ))}
      </section>
    );
  }

  return (
    <motion.section
      id="social-proof-banner"
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5 }}
      className={`grid grid-cols-2 gap-3 md:grid-cols-4 ${className}`}
      aria-label={lang === 'en' ? 'Inside Panama in numbers' : lang === 'es' ? 'Inside Panama en cifras' : 'Inside Panama en chiffres'}
    >
      {STATS.map((stat) => (
        <StatTile key={stat.id} stat={stat} lang={lang} shouldAnimate={shouldAnimate} />
      ))}
    </motion.section>
  );
}
